import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

// Keys used in localStorage
const STORAGE_KEY = "newbell_inquiry_list_v1";
const PROFILE_KEY = "newbell_inquiry_profile_v1";

const InquiryContext = createContext(null);

export function InquiryProvider({ children }) {
  const [items, setItems] = useState([]);
  const [userProfile, setUserProfile] = useState(null);

  // Initialize from localStorage
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) setItems(JSON.parse(raw));
    } catch (e) {
      console.error("Failed to read inquiry list from localStorage", e);
    }

    try {
      const rawProfile = localStorage.getItem(PROFILE_KEY);
      if (rawProfile) setUserProfile(JSON.parse(rawProfile));
    } catch (e) {
      console.error("Failed to read user profile from localStorage", e);
    }
  }, []);

  // Persist items to localStorage whenever items change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (e) {
      console.error("Failed to write inquiry list to localStorage", e);
    }
  }, [items]);

  // Persist profile to localStorage whenever profile changes
  useEffect(() => {
    try {
      if (userProfile) localStorage.setItem(PROFILE_KEY, JSON.stringify(userProfile));
      else localStorage.removeItem(PROFILE_KEY);
    } catch (e) {
      console.error("Failed to write user profile to localStorage", e);
    }
  }, [userProfile]);

  // small id generator
  const makeId = () => Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 9);

  const addInquiry = useCallback((payload) => {
    const item = {
      id: makeId(),
      createdAt: new Date().toISOString(),
      ...payload,
    };
    setItems((prev) => [item, ...prev]);
    return item;
  }, []);

  const updateInquiry = useCallback((id, patch) => {
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, ...patch } : i)));
  }, []);

  const removeInquiry = useCallback((id) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
  }, []);

  const clearInquiries = useCallback(() => {
    setItems([]);
  }, []);

  // Save the user's profile (buyer/contact info)
  const saveUserProfile = useCallback((profile) => {
    // profile should be an object matching the expected shape
    setUserProfile({
      ...profile,
      updatedAt: new Date().toISOString(),
    });
  }, []);

  // validation helper for required fields (Full name, Email, Phone)
  const validateUserProfile = useCallback((profile) => {
    const errors = {};
    if (!profile || !String(profile.fullName || "").trim()) {
      errors.fullName = "Full name is required";
    }
    const email = String(profile?.email || "").trim();
    // simple email regex (client-side)
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      errors.email = "Email is required";
    } else if (!emailRegex.test(email)) {
      errors.email = "Enter a valid email address";
    }

    const phone = String(profile?.phone || "").trim();
    if (!phone) {
      errors.phone = "Phone is required";
    } else if (phone.length < 5) {
      errors.phone = "Enter a valid phone number";
    }

    const ok = Object.keys(errors).length === 0;
    return { ok, errors };
  }, []);

  const value = {
    items,
    count: items.length,
    addInquiry,
    updateInquiry,
    removeInquiry,
    clearInquiries,
    userProfile,
    saveUserProfile,
    setUserProfile,
    validateUserProfile,
  };

  return <InquiryContext.Provider value={value}>{children}</InquiryContext.Provider>;
}

export function useInquiry() {
  const ctx = useContext(InquiryContext);
  if (!ctx) throw new Error("useInquiry must be used inside InquiryProvider");
  return ctx;
}